import { decode } from 'html-entities';
import type { AmazonItemDeal } from '../../types/deals';
import {
  AMAZON_STORE_LABEL,
  AMAZON_STORE_SLUG,
  dealPermalink,
  dealsPath,
  dealsPathStoreSlug,
  findDealBySlug,
  isAmazonStore,
  isExpiringSoon,
  isLatestDeal,
  isLiveDeal,
  matchesStore,
  type DealFilters,
} from './dealFilters';

// Region code formatPrice takes; /api/deals/amazon serves us only.
export const AMAZON_ITEM_DEALS_REGION = 'us';

export const AMAZON_ITEM_REFETCH_MS = 45 * 60 * 1000;

export const AMAZON_ITEM_RECHECK_MS = 60 * 1000;

// Licence: offer data is never shown past 24h (SERVE_LIMIT_MS on the server). Callers re-check rows every AMAZON_ITEM_RECHECK_MS and when the tab is shown again, not when a fetch lands, because a refetch that fails or never runs keeps the old rows. Stopping one refetch interval short of 24h leaves room for a client clock running behind the server's.
const AMAZON_ITEM_SHOW_LIMIT_MS = 24 * 60 * 60 * 1000 - AMAZON_ITEM_REFETCH_MS;

const AMAZON_STORE = { retailer: AMAZON_STORE_SLUG, retailerDisplayName: AMAZON_STORE_LABEL };

export function isShowableAmazonPrice(priceAsOf: number, now = Date.now()): boolean {
  return now - priceAsOf <= AMAZON_ITEM_SHOW_LIMIT_MS;
}

export function isShowableAmazonOffer(
  asOf: number,
  endTime: string | undefined,
  now = Date.now(),
): boolean {
  return isShowableAmazonPrice(asOf, now) && isLiveDeal({ expires_at: endTime ?? null }, now);
}

export function showableAmazonItems(items: AmazonItemDeal[], now = Date.now()): AmazonItemDeal[] {
  return items.filter((item) => isShowableAmazonOffer(item.priceAsOf, item.deal.endTime, now));
}

export function matchesAmazonItemFilters(
  item: AmazonItemDeal,
  filters: DealFilters,
  now = Date.now(),
): boolean {
  if (!matchesStore(AMAZON_STORE, filters.vendor)) return false;
  if (filters.hasCode) return false;
  if (filters.expiringSoon && !isExpiringSoon({ expires_at: item.deal.endTime ?? null }, now))
    return false;
  if (filters.latest && !isLatestDeal({ effective_at: item.deal.startTime ?? null }, now))
    return false;
  if (filters.discount != null && (item.deal.savingsPercent ?? 0) < filters.discount) return false;
  return true;
}

export function amazonItemPrice(item: AmazonItemDeal): number | null {
  if (item.deal.price != null) return item.deal.price;
  const parsed = item.price != null ? parseFloat(item.price) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function amazonItemTitle(item: Pick<AmazonItemDeal, 'asin' | 'title'>): string {
  return item.title ? decode(item.title) : item.asin;
}

// Titles are Amazon offer data, which must not outlive 24h; a URL lives on in history, shared links and analytics.
export function amazonItemSlug(item: Pick<AmazonItemDeal, 'asin'>): string {
  return item.asin.toLowerCase();
}

// Amazon follows "Ends in" with a live countdown the API does not send; the expiry pill covers it.
export function shownAmazonBadge(badge: string | undefined): string | null {
  if (!badge || badge.trim().toLowerCase() === 'ends in') return null;
  return badge;
}

/** Amazon lets the date be omitted only when the price was refreshed on the day it is shown. */
export function amazonPriceTimeFormat(priceAsOf: number, now = Date.now()): string {
  return new Date(priceAsOf).toDateString() === new Date(now).toDateString()
    ? 'h:mm A'
    : 'M/D/YYYY, h:mm A';
}

export function amazonItemPermalink(item: Pick<AmazonItemDeal, 'asin'>): string {
  return dealPermalink(AMAZON_STORE_SLUG, amazonItemSlug(item));
}

// Every ASIN and ISBN-10 has a digit, so a ten-letter word such as "membership" never reads as one.
const ASIN_SLUG_SEGMENT = /^(?=[a-z]*\d)[a-z0-9]{10}$/i;

/** Must stay in step with asinFromItemSlug in service/util/deals.ts. */
export function asinFromItemSlug(slug: string): string | null {
  const tail = slug.slice(slug.lastIndexOf('-') + 1);
  return ASIN_SLUG_SEGMENT.test(tail) ? tail.toUpperCase() : null;
}

/**
 * In-app stand-in for the server's gone-offer redirect. Null means stay on the page.
 * An Amazon item ASIN is not a curated slug. Pinning Amazon must not send /deals/amazon/:asin
 * to the store listing. Must stay in step with dealPagePath in service/util/deals.ts.
 */
type GoneCuratedDeal = Parameters<typeof findDealBySlug>[0][number] & {
  retailerDisplayName: string | null;
};

export function goneCuratedDealPath(
  deals: GoneCuratedDeal[],
  storeParam: string,
  dealParam: string,
  searchParams?: URLSearchParams,
): string | null {
  if (findDealBySlug(deals, storeParam, dealParam)) return null;
  if (isAmazonStore(storeParam) && asinFromItemSlug(dealParam)) return null;
  const knownStore = dealsPathStoreSlug(deals, storeParam);
  if (!knownStore) return null;
  return dealsPath(knownStore, searchParams);
}
